<template>
  <div v-if="students">
    <h1 style="margin: 0 0 4px">Students</h1>
    <div class="d-flex justify-content-between align-items-start mb-2">
      <hilo-button label="Add +" @click="navigateToAddStudent" />
      <input
        class="search-input"
        v-model="search"
        placeholder="Search"
        @input="filterStudents"
      />
    </div>
    <hilo-table :columns="columns" :data="filteredStudents" />
    <NotesModal
      v-if="showNotesModal && selectedRow && selectedRow.id"
      :isVisible="showNotesModal"
      :targetUserId="selectedRow?.user?.id"
      :targetName="`${selectedRow?.firstName} ${selectedRow?.lastName}`"
      noteTypeName="Students Page"
      @close="closeNotesModal"
    />
  </div>
</template>

<script>
import HiloTable from "src/components/HiloComponents/HiloTable.vue";
import HiloButton from "src/components/HiloComponents/HiloButton.vue";
import NotesModal from "src/components/HiloComponents/NotesModal.vue";
import ActiveStudentsAPIService from "src/servicehandlers/ActiveStudentsAPIService.js";
const activeStudentsAPIService = new ActiveStudentsAPIService();

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
dayjs.extend(utc);

export default {
  name: "Students",
  components: {
    NotesModal,
    HiloButton,
    HiloTable,
  },
  data() {
    return {
      students: [],
      filteredStudents: [],
      search: "",
      showNotesModal: false,
      selectedRow: {},
      columns: [
        {
          key: "edit",
          label: "Edit",
          component: "HiloButton",
          componentProps: (row) => ({
            styleType: "editIcon",
            onClick: () => this.navigateToEditStudent(row.id),
          }),
        },
        {
          key: "profile",
          label: "Profile",
          component: "HiloButton",
          componentProps: (row) => ({
            styleType: "profileIcon",
            onClick: () => this.navigateToStudentProfile(row.id),
          }),
        },
        {
          key: "image",
          label: "Image",
          component: "img",
          componentProps: (row) => ({
            src: row.image || "/static/img/faces/face-0.jpg",
            alt: `${row.firstName} ${row.lastName}`,
            style: {
              width: "3em",
              height: "3em",
              borderRadius: "50%",
            },
          }),
        },
        { key: "firstName", label: "First" },
        { key: "lastName", label: "Last" },
        { key: "startDate", label: "Start Date" },
        { key: "pplHoursDisplay", label: "PPL", backgroundColor: "#b7a5d5" },
        { key: "irHoursDisplay", label: "IR", backgroundColor: "#a9c0f3" },
        { key: "crewDisplay", label: "Crew", backgroundColor: "#a3c4c9" },
        { key: "comHoursDisplay", label: "COM", backgroundColor: "#b5d8a9" },
        { key: "cfiHoursDisplay", label: "CFI", backgroundColor: "#fce79c" },
        { key: "cfiiHoursDisplay", label: "CFII", backgroundColor: "#f7cc9e" },
        {
          key: "multiHoursDisplay",
          label: "MULTI",
          backgroundColor: "#db7f6d",
        },
        { key: "meiHoursDisplay", label: "MEI", backgroundColor: "#db7f8d" },
        {
          key: "flightHoursDisplay",
          label: "Flight",
          backgroundColor: "#d4a6bd",
        },
        { key: "simHoursDisplay", label: "SIM", backgroundColor: "#e99a9a" },
        {
          key: "totalHoursDisplay",
          label: "Total",
          backgroundColor: "#d4a6bd",
        },
        {
          key: "groundHoursDisplay",
          label: "Ground",
          backgroundColor: "#c27ba0",
        },
        { key: "balance", label: "Balance", backgroundColor: "#c27ba0" },
        {
          key: "note",
          label: "Notes",
          component: "HiloButton",
          componentProps: (row) => ({
            label: "View",
            onClick: () => this.openNotesModal(row),
          }),
        },
      ],
    };
  },
  async mounted() {
    await this.getStudents();
  },
  methods: {
    async getStudents() {
      return activeStudentsAPIService
        .get(this.$router)
        .then((response) => {
          const apiUrl = process.env.VUE_APP_API_URL;
          this.students = response;
          for (let student of this.students) {
            student.firstName = student.user.firstName;
            student.lastName = student.user.lastName;
            student.startDate = student.startDate
              ? dayjs(student.startDate).utc().format("MM/DD/YY")
              : "-";

            student.pplAllotted = student.pplAllotted || 0;
            student.pplHours = student.pplHours || 0;
            student.pplHoursDisplay = `${student.pplHours} / ${student.pplAllotted}`;

            student.irAllotted = student.irAllotted || 0;
            student.irHours = student.irHours || 0;
            student.irHoursDisplay = `${student.irHours} / ${student.irAllotted}`;

            student.crewAllotted = student.crewAllotted || 0;
            student.crewHours = student.crewHours || 0;
            student.crewDisplay = `${student.crewHours} / ${student.crewAllotted}`;

            student.comAllotted = student.comAllotted || 0;
            student.comHours = student.comHours || 0;
            student.comHoursDisplay = `${student.comHours} / ${student.comAllotted}`;

            student.cfiAllotted = student.cfiAllotted || 0;
            student.cfiHours = student.cfiHours || 0;
            student.cfiHoursDisplay = `${student.cfiHours} / ${student.cfiAllotted}`;

            student.cfiiAllotted = student.cfiiAllotted || 0;
            student.cfiiHours = student.cfiiHours || 0;
            student.cfiiHoursDisplay = `${student.cfiiHours} / ${student.cfiiAllotted}`;

            student.multiAllotted = student.multiAllotted || 0;
            student.multiHours = student.multiHours || 0;
            student.multiHoursDisplay = `${student.multiHours} / ${student.multiAllotted}`;

            student.meiAllotted = student.meiAllotted || 0;
            student.meiHours = student.meiHours || 0;
            student.meiHoursDisplay = `${student.meiHours} / ${student.meiAllotted}`;

            student.simAllotted = student.simAllotted || 0;
            student.simHours = student.simHours || 0;
            student.simHoursDisplay = `${student.simHours} / ${student.simAllotted}`;

            student.flightHours = Number(
              (
                student.pplHours +
                student.irHours +
                student.crewHours +
                student.comHours +
                student.cfiHours +
                student.cfiiHours +
                student.multiHours +
                student.meiHours
              ).toFixed(1),
            );
            student.flightAllotted = Number(
              (
                student.pplAllotted +
                student.irAllotted +
                student.crewAllotted +
                student.comAllotted +
                student.cfiAllotted +
                student.cfiiAllotted +
                student.multiAllotted +
                student.meiAllotted
              ).toFixed(1),
            );
            student.flightHoursDisplay = `${student.flightHours} / ${student.flightAllotted}`;

            student.totalHours = Number(
              (student.flightHours + student.simHours).toFixed(1),
            );
            student.totalAllotted = Number(
              (student.flightAllotted + student.simAllotted).toFixed(1),
            );
            student.totalHoursDisplay = `${student.totalHours} / ${student.totalAllotted}`;

            student.groundHours = student.groundHours || 0;
            student.groundAllotted = student.groundAllotted || 0;
            student.groundHoursDisplay = `${student.groundHours} / ${student.groundAllotted}`;

            student.balance = `$${this.formatBalance(student.balance)}`;
            if (student.user.image && typeof student.user.image === "string") {
              student.image = `${apiUrl}/file_uploads/${student.user.image.split("/").pop()}`;
            }
          }
          this.filteredStudents = this.students;
        })
        .catch((error) => {
          this.error_txt = "Failed to get students: " + error;
          //this.handleError();
        });
    },
    filterStudents() {
      const searchTerm = this.search.toLowerCase();
      const columnKeys = this.columns.map((column) => column.key);
      this.filteredStudents = this.students.filter((student) => {
        return columnKeys.some((key) => {
          const value = student[key];
          return value && value.toString().toLowerCase().includes(searchTerm);
        });
      });
    },
    formatBalance(value) {
      if (typeof value !== "number") {
        return 0;
      }

      return value.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
    navigateToAddStudent() {
      this.$router.push({ name: "Add Edit Active Student" });
    },
    navigateToEditStudent(id) {
      this.$router.push({ name: "Add Edit Active Student", params: { id } });
    },
    navigateToStudentProfile(id) {
      this.$router.push({ name: "Profile Student", params: { id } });
    },
    openNotesModal(row) {
      this.selectedRow = row;
      this.showNotesModal = true;
    },
    closeNotesModal() {
      this.showNotesModal = false;
      this.selectedRow = {};
    },
  },
};
</script>
<style scoped></style>
